import React, {
  useCallback, useEffect, useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { MdOutlineLogout } from 'react-icons/md';

import { getNotificationsAction } from '../../actions/notificationsActions';
import { getGifAction, getMeteoAction, getToolsAction } from '../../actions/userActions';
import useMajeeRights from '../../hooks/useMajeeRights';
import { SECTIONS } from '../../constants/majeeRights';

import logo from '../../assets/images/logo-nav.svg';
import styles from './Nav.module.scss';

export default function Nav({ logout }) {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const { userAuth } = useSelector((state) => state.auth);
  const { tools, gif } = useSelector((state) => state.user);
  const rights = useMajeeRights();

  const { firstName, lastName } = userAuth || {};
  const toolsList = tools?.toolsList;
  const [isActive, setIsActive] = useState(false);
  const day = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date());

  const getData = useCallback(() => {
    if (!gif) {
      getGifAction(dispatch, day);
      getMeteoAction(dispatch);
    }
  }, [dispatch, gif, day]);

  const getTools = useCallback(() => {
    getToolsAction(dispatch, userAuth);
    getNotificationsAction(dispatch);
  }, [dispatch, userAuth]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!toolsList && userAuth?.tools?.length) {
      getTools();
    }
  }, [userAuth?.tools, toolsList, getTools]);

  const newsActive = pathname.startsWith('/majee/actus');

  const links = [
    { path: '/majee/home', label: 'Accueil', always: true },
    { path: '/majee/planning', label: 'Planning', section: SECTIONS.PLANNING },
    {
      path: '/majee/actus',
      label: 'Actualités',
      section: SECTIONS.NEWS,
      children: [
        { path: '/majee/actus', label: 'Visualisation' },
        ...(rights.canEditNews
          ? [{ path: '/majee/actus/gestion', label: 'Gestion' }]
          : []),
      ],
    },
    { path: '/majee/tool', label: 'Outils', section: SECTIONS.TOOLS },
    { path: '/majee/aires', label: 'Aires thérapeutiques', section: SECTIONS.THERAPEUTIC_AREAS },
    { path: '/majee/produits', label: 'Produits', section: SECTIONS.PRODUCTS },
    { path: '/majee/pathologies', label: 'Pathologies', section: SECTIONS.PATHOLOGIES },
    { path: '/majee/centres', label: 'Centres & services', section: SECTIONS.CENTERS },
    { path: '/majee/contacts', label: 'Contacts médicaux', section: SECTIONS.CONTACTS },
    { path: '/majee/congres', label: 'Congrès', section: SECTIONS.CONGRESSES },
    { path: '/majee/project', label: 'Projets', section: SECTIONS.PROJECTS },
    { path: '/majee/concurrents', label: 'Fiches concurrents', section: SECTIONS.COMPETITORS },
  ].filter((l) => l.always || rights.canConsult(l.section));

  return (
    <>
      <button
        type="button"
        className={`${styles.toggle} ${isActive ? styles.active : ''}`}
        aria-label={isActive ? 'Fermer le menu' : 'Ouvrir le menu'}
        onClick={() => setIsActive(!isActive)}
      >
        <span />
        <span />
        <span />
      </button>
      {isActive && (
        <button
          type="button"
          className={styles.overlay}
          aria-label="Fermer le menu"
          onClick={() => setIsActive(false)}
        />
      )}
      <nav className={`${styles.container} ${isActive ? styles.open : ''}`}>
        <NavLink
          to={`/majee/home${search}`}
          className={styles.logo}
          onClick={() => setIsActive(false)}
        >
          <img src={logo} alt="logo-majee" />
        </NavLink>

        <ul className={styles.links}>
          {links.map((l) => (
            <li key={l.path}>
              {l.children ? (
                <>
                  <NavLink
                    to={l.path + search}
                    onClick={() => setIsActive(false)}
                    className={() => (newsActive ? styles['active-link'] : '')}
                  >
                    {l.label}
                  </NavLink>
                  {newsActive && (
                    <ul className={styles.sub}>
                      {l.children.map((c) => (
                        <li key={c.path}>
                          <NavLink
                            to={c.path + search}
                            end={c.path === '/majee/actus'}
                            onClick={() => setIsActive(false)}
                            className={(navData) => (navData.isActive ? styles['active-sub'] : '')}
                          >
                            {c.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <NavLink
                  to={l.path + search}
                  onClick={() => setIsActive(false)}
                  className={(navData) => (navData.isActive ? styles['active-link'] : '')}
                >
                  {l.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <div className={styles.profile}>
          <p>
            {firstName}
            {' '}
            {lastName}
          </p>
          <button
            type="button"
            className={styles.logout}
            onClick={() => logout()}
          >
            <span>Deconnexion</span>
            <MdOutlineLogout />
          </button>
        </div>
      </nav>
    </>
  );
}
